import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fetchAssignableRoles } from 'actions/roleActions';
import { resetUserEmailLookup } from 'actions/userActions';
import { getAccountsForUserReset } from 'actions/accountActions';

/**
 * Custom hook to manage Add User Modal lifecycle and data fetching.
 * Handles initialization, cleanup, and role fetching.
 *
 * @param {Object} params - Lifecycle parameters
 * @param {Array} params.assignRolesList - Current roles list
 * @param {string} params.assignedGrandfatherClientNumber - Grandfather client number
 * @param {boolean} params.createUserLoaded - Whether user creation completed
 * @param {Object} params.steps - Step enum constants
 * @param {Function} params.setStepId - Function to update current step
 */
export const useAddUserModalLifecycle = ({
  assignRolesList,
  assignedGrandfatherClientNumber,
  createUserLoaded,
  steps,
  setStepId,
}) => {
  const dispatch = useDispatch();

  // Initialize and cleanup modal state
  useEffect(() => {
    dispatch(resetUserEmailLookup());
    dispatch(getAccountsForUserReset());

    return () => {
      dispatch(resetUserEmailLookup());
      dispatch(getAccountsForUserReset());
    };
  }, [dispatch]);

  // Fetch assignable roles if not already loaded.
  // Only depends on assignedGrandfatherClientNumber: intent is to refetch when account
  // context changes, not whenever the list transitions through empty states.
  useEffect(() => {
    if (assignRolesList.length === 0) {
      dispatch(fetchAssignableRoles(assignedGrandfatherClientNumber));
    }
  }, [assignedGrandfatherClientNumber, dispatch]);

  // Transition to confirmation step when user creation completes
  useEffect(() => {
    if (createUserLoaded) {
      setStepId(steps.AddUserConfirmation);
    }
  }, [createUserLoaded, steps.AddUserConfirmation, setStepId]);
};
